import {
  businessCategorySelector,
  icvcPageSelector,
  partnerIdSelector,
  partnerSelector,
  policyNumberCleanRegexSelector,
  policyNumberFinalFormatSelector,
  selectShowPropertyOccupancy,
  stepOrderSelector,
} from 'redux/selectors/config';
import { call, put, select } from 'redux-saga/effects';
import {
  caseCreationEditModeSelector,
  caseCreationSelector,
  decisionTreeDropOffSelector,
  decisionTreeResultSelector,
  isPropertyOccupancySelector,
  shouldSkipContactDetailsSelector,
} from 'redux/selectors/caseCreation';
import { currentStepSelector, maxStepSelector } from 'redux/selectors/step';
import { push } from 'connected-react-router';
import { updateMaxStep } from 'redux/actions/stepActions';
import icvcActions from 'redux/actions/icvcActions';
import routes from 'Routes/routesConfig';
import caseCreationActions from 'redux/actions/caseCreationActions';
import { resetOpenCaseDataFromGtm } from 'redux/actions/gtmActions';
import {
  getStepsAndSubSteps,
  hasQuestionsForAccident,
} from './step-saga-helper';
import { routeSelector } from 'redux/selectors/router';
import { NORMAL_DROP_OFF } from 'constants/information';
import dayjs from 'dayjs';
import { BusinessCategory } from 'constants/synergy';

const { removeCaseCreation, toggleCreateCaseIsEditMode } = caseCreationActions;
const { getPolicies } = icvcActions;

const shouldGoToSubStep = ({ subStep, orders, step }) =>
  subStep >= 0 && subStep < orders[step].length - 1;
const isTheLastStep = ({ step, orders }) => step === orders.length;
const isTheFirstStep = ({ currentStep, currentSubStep }) =>
  currentStep === 0 && (currentSubStep === 0 || currentSubStep === undefined);
const getNextStep = (orders, step, increase = 1) => {
  const nextStepIndex = typeof step === 'number' ? step + increase : 0;
  const nextSubStep = typeof orders[nextStepIndex] !== 'string' ? 0 : undefined;

  const nextStepUrl =
    typeof orders[nextStepIndex] === 'object'
      ? orders[nextStepIndex][nextSubStep]
      : orders[nextStepIndex];

  return {
    nextStepIndex,
    nextSubStep,
    nextStepUrl,
  };
};

export function* shouldShowPropertyOccupancy() {
  const isPropertyOccupancy = yield select(isPropertyOccupancySelector);
  const dropout = yield select(decisionTreeDropOffSelector);
  const showPropertyOccupancy = yield select(selectShowPropertyOccupancy);
  return (
    showPropertyOccupancy &&
    (!isPropertyOccupancy || dropout?.toLowerCase() === NORMAL_DROP_OFF)
  );
}
export function* getPartnerId() {
  return yield select(partnerIdSelector);
}

function getPreviousStep(step) {
  if (step) {
    if (typeof step !== 'string') {
      return step[step.length - 1];
    } else {
      return step;
    }
  }
  return '/';
}

function* removeCheckUp(checkUp) {
  const businessCategory = yield select(businessCategorySelector);
  if (!checkUp && businessCategory === BusinessCategory.HOME) {
    yield put(removeCaseCreation('isCheckUp'));
  }
}

function* makeSurePolicyNumberMatchesICVCDatabase(policyNumber) {
  const policyNumberCleanRegex = yield select(policyNumberCleanRegexSelector);
  const policyNumberFinalFormat = yield select(policyNumberFinalFormatSelector);
  if (!policyNumber || !policyNumberCleanRegex || !policyNumberFinalFormat) {
    return policyNumber;
  }
  const policy_number = policyNumber.replace(
    new RegExp(policyNumberCleanRegex),
    '',
  );
  return policyNumberFinalFormat.replace('{policy_number}', policy_number);
}

export function* moveToNextStep() {
  const decisionTreeResult = yield select(decisionTreeResultSelector);
  const icvcPage = yield select(icvcPageSelector);
  const currentRoute = yield select(routeSelector);
  const shouldShowIncidentMorePage = yield shouldShowPropertyOccupancy();
  const orders = getStepsAndSubSteps(yield select(stepOrderSelector), {
    checkUp: decisionTreeResult?.checkUp,
    additionalQuestions: hasQuestionsForAccident(decisionTreeResult),
    incidentMore: shouldShowIncidentMorePage,
    synergyIncidentUnderstood: decisionTreeResult?.understood_page,
  });
  const { step, subStep } = yield select(currentStepSelector);
  const shouldSkipContactDetails = yield select(
    shouldSkipContactDetailsSelector,
  );

  yield call(removeCheckUp, decisionTreeResult?.checkUp);

  if (icvcPage === currentRoute) {
    const {
      email,
      firstName,
      incidentDate,
      lastName,
      policyNumber,
      pdlNumber,
      postalCode,
    } = yield select(caseCreationSelector);
    const partnerReference = yield select(partnerSelector);
    const policy_number = yield makeSurePolicyNumberMatchesICVCDatabase(
      policyNumber || pdlNumber,
    );
    yield put(
      getPolicies({
        partner_id: yield getPartnerId(),
        partner_reference: partnerReference,
        policy_number,
        incident_occurrence_date: dayjs(incidentDate).format(
          'YYYY-MM-DDTHH:mm:ss[Z]',
        ),
        address: {
          postal_code: postalCode,
        },
        policy_holder: {
          first_name: firstName,
          last_name: lastName,
          email,
        },
      }),
    );
  }

  let nextStepUrl = '';
  let nextStepIndex;
  let nextSubStep;

  if (isTheLastStep({ step, orders })) {
    return;
  }

  if (shouldGoToSubStep({ subStep, orders, step })) {
    nextStepIndex = step;
    nextSubStep = subStep + 1;
    nextStepUrl = orders[nextStepIndex][nextSubStep];
  } else {
    let nextData = getNextStep(orders, step);

    if (
      shouldSkipContactDetails &&
      nextData.nextStepUrl === routes.SYNERGY_CONTACT_DETAILS
    ) {
      nextData = getNextStep(orders, step, 2);
    }

    nextStepUrl = nextData.nextStepUrl;
    nextStepIndex = nextData.nextStepIndex;
    nextSubStep = nextData.nextSubStep;
  }

  const nextStepListIndex = orders
    .flat()
    .findIndex(step => step === nextStepUrl);
  const { index } = yield select(maxStepSelector);

  if ((index || 0) < nextStepListIndex) {
    yield put(updateMaxStep({ index: nextStepListIndex, step: nextStepUrl }));
  }

  if (isTheFirstStep({ nextStepIndex, nextSubStep })) {
    yield put(resetOpenCaseDataFromGtm());
  }

  yield put(push(nextStepUrl));
}

export function* moveToPreviousStep() {
  const decisionTreeResult = yield select(decisionTreeResultSelector);
  const shouldShowIncidentMorePage = yield shouldShowPropertyOccupancy();
  const order = getStepsAndSubSteps(yield select(stepOrderSelector), {
    checkUp: decisionTreeResult?.checkUp,
    additionalQuestions: hasQuestionsForAccident(decisionTreeResult),
    incidentMore: shouldShowIncidentMorePage,
    synergyIncidentUnderstood: decisionTreeResult?.understood_page,
  });
  const isEditMode = yield select(caseCreationEditModeSelector);
  const shouldSkipContactDetails = yield select(
    shouldSkipContactDetailsSelector,
  );
  const { step, subStep } = yield select(currentStepSelector);
  let previousStep = routes.SUMMARY;
  if (isEditMode) {
    yield put(toggleCreateCaseIsEditMode());
  } else {
    previousStep =
      subStep > 0
        ? order[step][subStep - 1]
        : yield getPreviousStep(order[step - 1]);

    if (
      shouldSkipContactDetails &&
      previousStep === routes.SYNERGY_CONTACT_DETAILS &&
      step > 1
    ) {
      previousStep = yield getPreviousStep(order[step - 2]);
    }
  }
  yield put(push(previousStep));
}
